/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import React, { Component, ReactNode } from 'react';
import { IUser } from '../../models';
import PaneHeadline from '../shared/paneHeadline';
import { faMap } from '@fortawesome/free-solid-svg-icons';
import { locale } from '../../common/localization/localizationService';
//import { Spinner2 } from '../shared/spinner2';
import { IRoute } from '../../models';
import Select from 'react-select';
import './smartMap.scss';
import { Drawing } from '../../services/genericMapService';
import SmartMapGrid from './smartMapGrid';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import ToggleButton from '../shared/customInputTypes/ToggleButton';
import {
  GenericMapAgreement,
  ISmartMapFilterType,
  SmartMapGridTypes,
} from '../../models/GenericMapModels.model';
import SmartMapDashboard from './smartMapStatistics/index';
import { SmartMapGridElement } from './smartMapGrid/smartMapGrid';
import { smartMapZoomLimit } from '../../appConfig';
import {
  RadioButton,
  RadioButtonChangeEvent,
} from '@progress/kendo-react-inputs';
import { calculateDrawnedArea } from '../../utils/genericMapUtils';
import {
  IGenericMapAction,
  IGenericMapActionType,
} from '../../models/SmartMapActions.model';
import SmartMapActionModal from './smartMapActionModal/smartMapActionModal';
import { Spinner2 } from '../shared/spinner2';

export interface ISmartMapProps {
  genericMapActions: any;
  loadRoutes: any;
  authentication: IUser;
  isLoading: boolean;
  routes: Array<IRoute>;
  selectedRouteIds: Array<string>;
  genericMapDrawnShapes: Array<Drawing>;
  mapCorners: Array<Drawing>;
  isAgreementsVisible: boolean;
  zoomLevel: number;
  storedAgreements: GenericMapAgreement[];
  filterType: ISmartMapFilterType;
  smartMapActionTypes: IGenericMapActionType[];
  selectedSmartMapAction: IGenericMapAction;
}

export interface IRouteDataSource extends IRoute {
  value: string;
  label: string;
}

export interface ISmartMapState {
  routesDataSource: Array<IRouteDataSource>;
  selectedRoutes: Array<IRouteDataSource>;
  activeTab: number;
  selectedActionType: any;
  selectedGridElements: SmartMapGridElement[];
  actionTypeOptions: any[];
  showActionModal: boolean;
  selectedAction: IGenericMapAction;
}

export class SmartMap extends Component<ISmartMapProps, ISmartMapState> {
  public readonly state: ISmartMapState = {
    selectedRoutes: [],
    routesDataSource: [],
    activeTab: 0,
    selectedActionType: null,
    selectedGridElements: [],
    actionTypeOptions: [],
    showActionModal: false,
    selectedAction: null,
  };
  private dataDebounceTimer: number | null = null;
  private routeDebounceTimer: NodeJS.Timeout | null = null;

  public componentDidMount(): void {
    this.init();
  }

  public componentDidUpdate(prev: Readonly<ISmartMapProps>): void {
    // If they just turned OFF agreements, or zoom went below limit, cancel any pending fetch:
    const droppedBelowLimit =
      prev.zoomLevel !== this.props.zoomLevel &&
      this.props.zoomLevel != null &&
      this.props.zoomLevel < smartMapZoomLimit;
    const hidAgreements =
      prev.isAgreementsVisible && !this.props.isAgreementsVisible;

    if ((droppedBelowLimit || hidAgreements) && this.dataDebounceTimer) {
      clearTimeout(this.dataDebounceTimer);
      this.dataDebounceTimer = undefined;
    }

    if (
      prev.mapCorners !== this.props.mapCorners &&
      this.props.isAgreementsVisible
    ) {
      if (!this.props.zoomLevel || this.props.zoomLevel < smartMapZoomLimit) {
        this.props.genericMapActions.setZoomWarningVisibility(false);
        this.props.genericMapActions.setAgreementsVisible(false);
      } else {
        if (this.dataDebounceTimer) clearTimeout(this.dataDebounceTimer);
        this.dataDebounceTimer = window.setTimeout(() => {
          // (Optionally keep the guard here too, for extra safety)
          if (
            this.props.isAgreementsVisible &&
            this.props.zoomLevel != null &&
            this.props.zoomLevel >= smartMapZoomLimit
          ) {
            this.getAgreements();
          }
        }, 500);
      }
    }

    // “immediate” fetch if they just toggled visibility on:
    if (!prev.isAgreementsVisible && this.props.isAgreementsVisible) {
      this.getAgreements();
    }
    if (
      (this.props.filterType === ISmartMapFilterType.FORM &&
        prev.genericMapDrawnShapes !== this.props.genericMapDrawnShapes) ||
      prev.filterType !== this.props.filterType
    ) {
      this.getAgreements();
    }
  }

  actionOptions = [
    {
      value: 'textMessage',
      label: locale.smartMap.actionOptions._sendTextMessage,
    },
  ];
  private async init() {
    await this.props.loadRoutes();
    await this.props.genericMapActions.getActionTypes();

    const actionOptions = this.props.smartMapActionTypes.map((type) => {
      return {
        label: type.name,
        value: type.typeId,
      };
    });

    this.setState({ ...this.state, actionTypeOptions: actionOptions });

    const routesDataSource = this.props.routes.map((route) => {
      return {
        ...route,
        value: route.routeNumbder.toString(),
        label: route.name,
      };
    });

    this.setState({
      ...this.state,
      routesDataSource,
      selectedRoutes: this.getSelectedRoutes(
        this.props.selectedRouteIds,
        routesDataSource
      ),
    });
    this.props.genericMapActions.getRoutes(this.props.selectedRouteIds);
  }

  private handleRouteChange = (routes: any) => {
    this.setState({
      ...this.state,
      selectedRoutes: routes,
    });
    if (this.routeDebounceTimer) clearTimeout(this.routeDebounceTimer);
    this.routeDebounceTimer = setTimeout(() => {
      const ids = routes.map((route: IRouteDataSource) => route.routeNumbder);
      this.props.genericMapActions.getRoutes(ids);
    }, 500);
  };

  private handleActionChange = (actionType: any) => {
    this.setState({
      ...this.state,
      selectedActionType: actionType,
    });
  };

  private getSelectedRoutes(ids: Array<string>, routeDataSource: Array<any>) {
    const selectedRoutes: any[] = [];
    ids.forEach((id) => {
      const selectedRoute = routeDataSource.find(
        (route) => route.routeNumbder === id
      );
      selectedRoutes.push(selectedRoute);
    });
    return selectedRoutes;
  }

  private onGetAgreements() {
    if (
      (!this.props.isAgreementsVisible && !this.props.zoomLevel) ||
      this.props.zoomLevel < smartMapZoomLimit
    ) {
      this.props.genericMapActions.setZoomWarningVisibility(false);
    } else if (
      this.props.zoomLevel &&
      this.props.zoomLevel >= smartMapZoomLimit
    ) {
      this.props.genericMapActions.setAgreementsVisible(
        !this.props.isAgreementsVisible
      );
    }
  }

  private getAgreements() {
    if (
      this.props.isAgreementsVisible &&
      this.props.zoomLevel &&
      this.props.zoomLevel >= smartMapZoomLimit
    ) {
      if (this.props.filterType === ISmartMapFilterType.EXTENT) {
        const coordinates = this.props.mapCorners;
        this.props.genericMapActions.getData(coordinates, this.props.routes);
      } else if (
        this.props.filterType === ISmartMapFilterType.FORM &&
        !this.checkDrawnedShapesArea()
      ) {
        const coordinates = this.props.genericMapDrawnShapes;
        this.props.genericMapActions.getData(coordinates, this.props.routes);
      } else if (
        this.props.filterType === ISmartMapFilterType.ROUTE &&
        this.state.selectedRoutes.length > 0
      ) {
        const coordinates = this.props.mapCorners;
        this.props.genericMapActions.getData(coordinates, this.props.routes);
      }
    }
  }

  private checkDrawnedShapesArea(): boolean {
    let isShapesTooBig = false;
    const areas = calculateDrawnedArea(this.props.genericMapDrawnShapes);
    areas.forEach((area) => {
      if (area > 660000) {
        isShapesTooBig = true;
      }
    });
    return isShapesTooBig;
  }

  private setActiveTab(selected: number) {
    this.setState({ ...this.state, activeTab: selected });
  }

  private useAction() {
    this.props.genericMapActions.getActionByTypeId(
      this.state.selectedActionType.value
    );
    this.setState({
      ...this.state,
      showActionModal: true,
    });
  }

  private onSelectedGridElements(
    selectedElements: SmartMapGridElement[]
  ): void {
    this.setState({ ...this.state, selectedGridElements: selectedElements });
  }

  private handleFilterType(event: RadioButtonChangeEvent): void {
    this.props.genericMapActions.setFilterType(event.value);
  }

  private hideActionModal(rerender = false) {
    console.log(rerender);
    this.setState({ ...this.state, showActionModal: false });
  }

  public render(): ReactNode {
    return (
      <div id="smartMapInfos">
        <PaneHeadline titleText={locale.general._smartMap} titleIcon={faMap} />
        <>
          <Spinner2 show={this.props.isLoading} />
          <Select
            options={this.state.routesDataSource}
            value={this.state.selectedRoutes}
            onChange={this.handleRouteChange}
            isMulti
            placeholder={locale.orderOverview._chooseRoutes}
            className="input-field react-select"
            closeMenuOnSelect={false}
          />
          <div className="actions-wrapper">
            <div className="show-agreements">
              <ToggleButton
                onToggle={() => {
                  this.onGetAgreements();
                }}
                value={this.props.isAgreementsVisible}
              />
              <div className="show-agreement-label">
                {locale.smartMap._showData}
              </div>
            </div>
            <div className="filter-agreements">
              <RadioButton
                disabled={
                  !this.props.zoomLevel ||
                  this.props.zoomLevel < smartMapZoomLimit
                }
                name="form"
                value={ISmartMapFilterType.FORM}
                checked={this.props.filterType === ISmartMapFilterType.FORM}
                label={locale.smartMap._filterByForm}
                onChange={(e) => this.handleFilterType(e)}
              />
              <RadioButton
                disabled={
                  !this.props.zoomLevel ||
                  this.props.zoomLevel < smartMapZoomLimit
                }
                name="route"
                value={ISmartMapFilterType.ROUTE}
                checked={this.props.filterType === ISmartMapFilterType.ROUTE}
                label={locale.smartMap._filterByRoutue}
                onChange={(e) => this.handleFilterType(e)}
              />
              <RadioButton
                disabled={
                  !this.props.zoomLevel ||
                  this.props.zoomLevel < smartMapZoomLimit
                }
                name="extent"
                value={ISmartMapFilterType.EXTENT}
                checked={this.props.filterType === ISmartMapFilterType.EXTENT}
                label={locale.smartMap._filterByExtent}
                onChange={(e) => this.handleFilterType(e)}
              />
            </div>
          </div>
          <div className="smart-map-grid-wrapper">
            <TabStrip
              selected={this.state.activeTab}
              onSelect={(e) => this.setActiveTab(e.selected)}
            >
              <TabStripTab title={locale.smartMap.tabViews._dashboard}>
                {this.state.activeTab === 0 && <SmartMapDashboard />}
              </TabStripTab>
              <TabStripTab title={locale.smartMap.tabViews._agreements}>
                {this.state.activeTab === 1 && (
                  <SmartMapGrid
                    gridType={SmartMapGridTypes.AGREEMENTS_GRID}
                    setSelectedElements={(elements) =>
                      this.onSelectedGridElements(elements)
                    }
                  />
                )}
              </TabStripTab>
              <TabStripTab title={locale.smartMap.tabViews._places}>
                {this.state.activeTab === 2 && (
                  <SmartMapGrid
                    gridType={SmartMapGridTypes.PLACES_GRID}
                    setSelectedElements={(elements) =>
                      this.onSelectedGridElements(elements)
                    }
                  />
                )}
              </TabStripTab>
              <TabStripTab title={locale.smartMap.tabViews._bins}>
                {this.state.activeTab === 3 && (
                  <SmartMapGrid
                    gridType={SmartMapGridTypes.BINS_GRID}
                    setSelectedElements={(elements) =>
                      this.onSelectedGridElements(elements)
                    }
                  />
                )}
              </TabStripTab>
            </TabStrip>
          </div>
          {this.state.activeTab !== 0 && (
            <div className="smart-map-actions">
              <Select
                options={this.state.actionTypeOptions}
                value={this.state.selectedActionType}
                onChange={this.handleActionChange}
                className="input-field react-select "
                menuPlacement="top"
                placeholder={locale.smartMap._select}
              />
              <button
                className="btn btn-outline-dark ml-1 mr-1"
                onClick={() => this.useAction()}
                disabled={!this.state.selectedActionType}
              >
                {locale.smartMap._use}
              </button>
            </div>
          )}
          {this.state.showActionModal && (
            <>
              <SmartMapActionModal
                action={this.props.selectedSmartMapAction}
                show={this.state.showActionModal}
                selectedElements={this.state.selectedGridElements}
                onHide={this.hideActionModal.bind(this)}
                isLoading={this.props.isLoading}
                agreementIds={this.props.storedAgreements.map(
                  (a) => a.agreementId
                )}
              />
            </>
          )}
        </>
      </div>
    );
  }
}

export default SmartMap;
